import { Cell, ColumnDef, createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import merge from "lodash/merge";

import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { ERN, ERNBomLine } from "@/lib/supabase/edge-functions/erns";
import { JobTrackingInternalLink } from "../JobTrackingInternalLink";

export interface ERNTableRow {
  rowId: string;
  ern: ERN;
  line: ERNBomLine;
}

// For ERN columns, set the rowspan of each cell on the on the first line to the total number of lines. Hide all subsequent rows for the same ERN
const ernColCellProps = (
  cell: Cell<ERNTableRow, unknown>,
  additionalProps?: Omit<React.TdHTMLAttributes<HTMLTableCellElement>, "rowSpan">,
) => {
  const { ern, line } = cell.row.original;
  return line.lineNumber === 0 ?
      { ...additionalProps, rowSpan: ern.bomData.length }
    : { ...additionalProps, className: "d-none " + (additionalProps?.className ?? "") };
};

// For BOM columns, apply a class to the table cell indicating the recursive depth of the row
const bomColCellProps = (
  cell: Cell<ERNTableRow, unknown>,
  additionalProps?: React.TdHTMLAttributes<HTMLTableCellElement>,
) => {
  const depth = cell.row.original.line.hierarchyLevel ?? 0;
  return { ...additionalProps, className: `tree-depth-${depth.toString()} ${additionalProps?.className ?? ""}` };
};

// Create helpers for defining columns
const columnHelper = createColumnHelper<ERNTableRow>();
const ernAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: ernColCellProps } }, column));
const bomAccessorCol: typeof columnHelper.accessor = (accessor, column) =>
  columnHelper.accessor(accessor, merge({ meta: { cellProps: bomColCellProps } }, column));

const ernColumns = [
  ernAccessorCol("ern.number", { header: "ERN #" }),
  ernAccessorCol("ern.description", { header: "Description" }),
  ernAccessorCol("ern.createdBy", { header: "Created By" }),
];

const bomColumns = [
  bomAccessorCol("line.itemName", {
    header: "Item",
    cell: ({ row }) => {
      const itemId = row.original.line.itemId;
      const indent = <pre className="d-inline">{"   ".repeat(row.original.line.hierarchyLevel ?? 0)}</pre>;
      const label = (
        <>
          {indent}
          {row.original.line.itemName}
        </>
      );
      return itemId ?
          <JobTrackingInternalLink trackerPath="bom-by-component">
            {({ targetPath, targetPageTitle }) => (
              <SOSEntityHashLink
                fromHash={row.id}
                urlPath={targetPath}
                tooltipContent={`View on '${targetPageTitle}' page`}
                type="item"
                id={itemId}
                className="text-decoration-none text-nowrap"
                label={label}
              />
            )}
          </JobTrackingInternalLink>
        : label;
    },
    meta: {
      headerCellProps: { className: "border-start-double" },
      cellProps: (cell) => bomColCellProps(cell, { className: "border-start-double" }),
    },
  }),
  bomAccessorCol("line.description", { header: "Description" }),
  bomAccessorCol("line.quantity", {
    header: "Quantity",
  }),
  bomAccessorCol("line.unitOfMeasure", { header: "UOM" }),
  bomAccessorCol("line.actionRequired", {
    header: "Action Required",
    cell: ({ getValue }) => {
      const action = getValue();
      const colorClasses =
        action === "Add" ? "bg-success text-success-subtle"
        : action === "Remove" ? "bg-danger text-danger-subtle"
        : action === "See Comment" ? "bg-info text-info-subtle"
        : null;
      return <span className={classNames("badge", colorClasses)}>{action}</span>;
    },
    meta: { cellProps: (cell) => bomColCellProps(cell, { className: "text-center align-middle" }) },
  }),
  bomAccessorCol("line.comments", {
    header: "Comments",
  }),
];

// Export the full set of columns for the table
// NOTE: We have to define the group columns separately before this because the type inference for the group columns is broken in Tanstack Table
// See: https://github.com/TanStack/table/issues/5065
export const ernTableColumns: ColumnDef<ERNTableRow>[] = [
  columnHelper.group({
    header: "ERNs",
    columns: ernColumns,
  }),
  columnHelper.group({
    header: "BOM Data",
    columns: bomColumns,
    meta: {
      headerCellProps: { className: "border-start-double" },
    },
  }),
];
